<template>
  <table class="table-hover reports-table daily-reports-table table">
    <tbody class="thead">
      <tr>
        <slot name="thead" />
      </tr>
    </tbody>

    <tbody class="tbody">
      <tr
        v-for="row in tableData.grouped"
        :key="row.main_id"
      >
        <td>
          {{ row.main }}
        </td>
        <td v-if="tableShowAboutMember">
          <MemberAboutData
            :about-fields="aboutFields"
            :member-data="row"
            :user-name="row.main"
            :user-id="row.main_id"
            custom-visible-by-default
          />
        </td>
        <td
          v-for="date in isoDateRange"
          :key="date"
          class="text-right"
        >
          <template v-if="row.days[date]">
            <DailyReportTablePopover
              :overtimes="row.tracked_overtime[date]"
              :time-off-times="row.tracked_time_off[date]"
              :show-financial="isOwner"
              :currency="organization.currency"
            />
            {{ row.days[date] | formatDuration('user', '-', false, true) }}
          </template>
          <template v-else>--</template>
        </td>
        <td
          v-if="showOvertime"
          class="text-right"
        >
          {{ formatDuration(row.regular_tracked) }}
        </td>
        <td
          v-if="showOvertime"
          class="text-right"
        >
          <DailyReportTablePopover
            :overtimes="row.all_tracked_overtime"
            :show-financial="isOwner"
            :currency="organization.currency"
          />
          {{ formatDuration(row.overtime) }}
        </td>
        <td
          v-if="showHolidays"
          class="text-right"
        >
          {{ formatDuration(row.holidays) }}
        </td>
        <td
          v-if="showTimeOff"
          class="text-right"
        >
          <DailyReportTablePopover
            :time-off-times="row.all_tracked_time_off"
            :show-financial="isOwner"
            :currency="organization.currency"
          />
          {{ formatDuration(row.time_off) }}
        </td>
        <td class="text-right">{{ formatDuration(row.tracked) }}</td>
        <td
          v-if="showSpentRegular"
          class="text-right"
        >
          {{ row.spent_regular | currency(organization.currency, '—') }}
        </td>
        <td
          v-if="showSpentOvertime"
          class="text-right"
        >
          {{ row.spent_overtime | currency(organization.currency, '—') }}
        </td>
        <td
          v-if="showSpentHolidays"
          class="text-right"
        >
          {{ row.spent_holidays | currency(organization.currency, '—') }}
        </td>
        <td
          v-if="showSpentTimeOff"
          class="text-right"
        >
          {{ row.spent_time_off | currency(organization.currency, '—') }}
        </td>
        <td
          v-if="showActivity"
          class="text-right"
        >
          {{ row.overall | percentage(row.input_tracked) }}
        </td>
        <td
          v-if="showSpent"
          class="text-right"
        >
          <div class="d-flex align-items-center justify-content-end gap-4">
            <template>
              {{ row.spent | currency(organization.currency, blankValue) }}
            </template>
          </div>
        </td>
        <td v-else></td>
      </tr>
    </tbody>

    <tbody
      v-if="showFooter"
      class="tfoot"
    >
      <tr>
        <th>Daily total</th>
        <td v-if="tableShowAboutMember"></td>
        <td
          v-for="date in isoDateRange"
          :key="date"
          class="text-right"
        >
          {{ formatDuration(tableData.total.days[date]) }}
        </td>
        <td
          v-if="showOvertime"
          class="text-right"
        >
          {{ formatDuration(tableData.total.regular_tracked) }}
        </td>
        <td
          v-if="showOvertime"
          class="text-right"
        >
          {{ formatDuration(tableData.total.overtime) }}
        </td>
        <td
          v-if="showHolidays"
          class="text-right"
        >
          {{ formatDuration(tableData.total.holidays) }}
        </td>
        <td
          v-if="showTimeOff"
          class="text-right"
        >
          {{ formatDuration(tableData.total.time_off) }}
        </td>
        <td class="text-right">{{ formatDuration(tableData.total.tracked) }}</td>
        <td
          v-if="showSpentRegular"
          class="text-right"
        >
          {{ tableData.total.spent_regular | currency(organization.currency) }}
        </td>
        <td
          v-if="showSpentOvertime"
          class="text-right"
        >
          {{ tableData.total.spent_overtime | currency(organization.currency) }}
        </td>
        <td
          v-if="showSpentHolidays"
          class="text-right"
        >
          {{ tableData.total.spent_holidays | currency(organization.currency) }}
        </td>
        <td
          v-if="showSpentTimeOff"
          class="text-right"
        >
          {{ tableData.total.spent_time_off | currency(organization.currency) }}
        </td>
        <td
          v-if="showActivity"
          class="text-right"
        >
          {{ tableData.total.overall | percentage(tableData.total.input_tracked) }}
        </td>
        <td class="text-right">
          <template v-if="showSpent">{{ tableData.total.spent | currency(organization.currency) }}</template>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import currency from '~/filters/currency.js';
import DailyReportTablePopover from '~/components/reports/daily/DailyReportTablePopover.vue';
import { formatDuration } from '~/utils/time_utils';
import MemberAboutData from '~/components/members/MemberAboutData.vue';

export default {
  name: 'DailyReportTable',
  components: { DailyReportTablePopover, MemberAboutData },
  props: {
    tableShowAboutMember: { type: Boolean, required: true },
    tableData: { type: Object, required: true },
    dateRange: { type: Array, required: true },
    aboutFields: { type: Object, required: true },
    showActivity: { type: Boolean, default: false },
    showHolidays: { type: Boolean, default: false },
    showOvertime: { type: Boolean, required: true },
    showTimeOff: { type: Boolean, default: false },
    showSpentRegular: { type: Boolean, default: false },
    showSpentOvertime: { type: Boolean, default: false },
    showSpentHolidays: { type: Boolean, default: false },
    showSpentTimeOff: { type: Boolean, default: false },
    showSpent: { type: Boolean, default: false },
    showFooter: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    ...mapGetters(['isOwner', 'tableIsTeam', 'managePaymentInfo', 'viewPaymentInfo']),
    ...mapState({
      organization: 'organization',
    }),
    isoDateRange() {
      return this.dateRange.map((date) => date.iso);
    },
    blankValue() {
      return (this.viewPaymentInfo && !this.managePaymentInfo) ? '—' : '';
    },
  },
  methods: {
    formatDuration(duration, options = {}) {
      const defaultOptions = {
        blank: '-',
        roundSeconds: true,
      };
      const mergedOptions = { ...defaultOptions, ...options };

      return formatDuration(duration, mergedOptions);
    },
    currency(amount) {
      return currency(amount, this.organization.currency);
    },
    mixedRates(row) {
      return [
        row.spent_regular && `${this.showOvertime ? 'Regular ' : ''} hours: ` + this.currency(row.spent_regular, '—'),
        this.showOvertime && row.spent_overtime && 'Overtime: ' + this.currency(row.spent_overtime, '—'),
        row.spent_holidays && 'Holidays: ' + this.currency(row.spent_holidays, '—'),
        row.spent_time_off && 'PTO: ' + this.currency(row.spent_time_off, '—'),
        row.paid_work_breaks && 'Work break: ' + this.currency(row.paid_work_breaks / 3600, '—'),
      ]
        .filter(Boolean)
        .join('<br>');
    },
  },
};
</script>
